import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedDate } from 'react-intl';
import { Link } from 'react-router-dom';

import { GhostButton, IconButton } from '@bibliocommons/base-buttons';
import { Delete as IconDelete, Edit as IconEdit, Plus as IconPlus } from '@bibliocommons/base-icons';
import { Link as BaseLink } from '@bibliocommons/base-links';
import Heading from '@bibliocommons/heading';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@bibliocommons/modal';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';

import { ROUTE } from 'app/constants/AdminBrowseConstants';
import { browseHomePageShape } from 'app/shapes/admin/browse/home';

import './AdminBrowseHomePage.scss';

const AdminBrowseHomePage = ({ browseHomePages, isSaving, onActivate, onDelete }) => {
  const [browseHomePageIdToDelete, setBrowseHomePageIdToDelete] = useState(null);

  const openConfirmDeleteModal = browseHomePageId => {
    setBrowseHomePageIdToDelete(browseHomePageId);
  };

  const closeConfirmDeleteModal = () => {
    setBrowseHomePageIdToDelete(null);
  };

  const handleDeleteClick = () => {
    onDelete(browseHomePageIdToDelete);
    closeConfirmDeleteModal();
  };

  const isConfirmationModalOpen = Boolean(browseHomePageIdToDelete);
  const nameOfPageToDelete = isConfirmationModalOpen
    ? browseHomePages.find(browseHomePage => {
        return browseHomePage.browseHomePageId === browseHomePageIdToDelete;
      })?.name ?? ''
    : '';

  return (
    <div className="cp-admin-browse-home-page container">
      <Heading className="heading" size="large" tagName="h1" variant>
        Browse Home
      </Heading>

      <Modal handleClose={closeConfirmDeleteModal} key="BrowseAdminHome_ConfirmDelete" open={isConfirmationModalOpen}>
        <ModalHeader title="Delete page" />
        <ModalContent>
          <p>Are you sure you want to delete the page &apos;{nameOfPageToDelete}&apos;?</p>
        </ModalContent>
        <ModalFooter handlePrimaryAction={handleDeleteClick} primaryActionButtonText="Delete page" />
      </Modal>

      <Table>
        <TableHeader>
          <TableHeading>Name</TableHeading>
          <TableHeading>Creator</TableHeading>
          <TableHeading>Language</TableHeading>
          <TableHeading>Last updated</TableHeading>
          <TableHeading>Status</TableHeading>
          <TableHeading />
        </TableHeader>
        <TableBody>
          {browseHomePages.map(browseHomePage => {
            const { browseHomePageId, createdBy, isActive, localeCode, name, updatedAt, updatedBy } = browseHomePage;

            return (
              <TableRow key={browseHomePageId}>
                <TableCell className="home-page-name">{name}</TableCell>
                <TableCell>{createdBy}</TableCell>
                <TableCell>{localeCode}</TableCell>
                <TableCell>
                  <FormattedDate day="2-digit" month="long" value={updatedAt} year="numeric" /> by {updatedBy}
                </TableCell>
                <TableCell>
                  <span className="actions">
                    <span>{isActive ? 'Active' : 'Draft'}</span>
                    {!isActive && (
                      <GhostButton
                        className="action"
                        dataKey="action-activate"
                        handleClick={() => onActivate(browseHomePageId)}
                        isLoading={isSaving}
                      >
                        Activate
                      </GhostButton>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="actions">
                    <Link
                      className="action action--link-icon"
                      dataKey={`${ROUTE.ADMIN_BROWSE_HOME_EDIT}/${browseHomePageId}`}
                      to={`${ROUTE.ADMIN_BROWSE_HOME_EDIT}/${browseHomePageId}`}
                    >
                      <IconEdit />
                    </Link>
                    <IconButton
                      className="action"
                      dataKey="action-delete"
                      handleClick={() => openConfirmDeleteModal(browseHomePageId)}
                      isLoading={isSaving}
                    >
                      <IconDelete />
                    </IconButton>
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <BaseLink className="create-new" href={ROUTE.ADMIN_BROWSE_HOME_CREATE}>
        <IconPlus className="icon-plus" />
        Create new home page
      </BaseLink>
    </div>
  );
};

AdminBrowseHomePage.propTypes = {
  browseHomePages: PropTypes.arrayOf(browseHomePageShape).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onActivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default AdminBrowseHomePage;
